import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import { useData } from '../contexts/DataContext';
import { isTextSafe } from '../utils/safeForAds';

const CreateModulePage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { refreshModules } = useData();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async (from, to, dialect, name) => {
    if (dialect && dialect.length > 50) {
      setError(t('dialectMaxLength') || 'Dialect cannot exceed 50 characters');
      return;
    }
    // Check for profanity in dialect
    if (dialect && !isTextSafe(dialect.trim())) {
      setError(t('inappropriateContentDetected') || 'Inappropriate content detected. Please use appropriate language.');
      return;
    }
    if (!name || !name.trim()) {
      setError(t('moduleNameRequired') || 'Module name is required');
      return;
    }
    if (name.trim().length > 150) {
      setError(t('moduleNameMaxLength') || 'Module name cannot exceed 150 characters');
      return;
    }
    // Check for profanity in module name
    if (!isTextSafe(name.trim())) {
      setError(t('inappropriateContentDetected') || 'Inappropriate content detected. Please use appropriate language.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setError(t('pleaseLogIn'));
        setLoading(false);
        return;
      }
      // Get the current maximum position for this user
      const { data: existingModules, error: countError } = await supabase
        .from('modules_new')
        .select('position')
        .eq('user_id', userId)
        .order('position', { ascending: false })
        .limit(1);
      if (countError) {
        console.error('Error fetching module positions:', countError);
        setError(t('couldNotCreateModule') || 'Could not create module.');
        setLoading(false);
        return;
      }
      const position = (!existingModules || existingModules.length === 0)
        ? 0
        : (existingModules[0].position + 1);
      // Insert new module into modules_new
      const { data: inserted, error: insertError } = await supabase
        .from('modules_new')
        .insert({
          user_id: userId,
          from_lang: from,
          to_lang: to,
          dialect: dialect ? dialect.trim().substring(0, 50) : '',
          name: name.trim().substring(0, 150),
          position,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
      if (insertError || !inserted) {
        setError(t('couldNotCreateModule') || 'Could not create module.');
        setLoading(false);
        return;
      }
      // Refresh modules and navigate to the new module
      refreshModules();
      navigate(`/module/${inserted.id}`);
      setLoading(false);
    } catch (error) {
      setError(t('couldNotCreateModule') || 'Could not create module.');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  return (
    <div className="card">
      <h2>{t('createANewModule')}</h2>
      <LanguageSelector onSubmit={handleCreate} onCancel={handleCancel} showDialect />
      {error && <div className="error-message">{error}</div>}
      {loading && <div className="loading-message">{t('creating') || 'Creating...'}</div>}
    </div>
  );
};

export default CreateModulePage; 